import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { mixed, object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import { AulaLista } from 'Component/Aula';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, {
  Arquivo,
  Botao as BotaoForm,
  CaixaDeTexto,
  Campo,
  Selecao,
} from 'Component/Form';
import LayoutDefault from 'Component/Layout';
import Modal from 'Component/Modal';
import Aula from 'Interface/Aulas';
import Treinamento from 'Interface/Treinamentos';
import { useAdicionarAulaMutation } from 'View/Service/Default/Aulas';
import { useRecuperarDepartamentosQuery } from 'View/Service/Default/Departamentos';
import {
  useEditarTreinamentoMutation,
  useRecuperarTreinamentoQuery,
} from 'View/Service/Default/Treinamentos';

const regras = object({
  titulo: string().required('Obrigatório'),
  departamento_id: string().required('Obrigatório'),
  descricao: string().nullable().optional(),
});

const regrasAula = object({
  titulo: string().required('Obrigatório'),
  descricao: string().nullable().optional(),
  url: mixed().required('Obrigatório'),
});

const Editar = (): React.ReactElement => {
  const [modal, setModal] = useState<boolean>(false);
  const toast = useToast();
  const { id = 0 } = useParams();
  const [editarTreinamento] = useEditarTreinamentoMutation();
  const [adicionarAula] = useAdicionarAulaMutation();

  const { data: { departamentos } = {} } = useRecuperarDepartamentosQuery({
    finder: 'listaJson',
    sort: 'nome',
    limit: 200,
  });
  const { data: { treinamento } = {}, isLoading } =
    useRecuperarTreinamentoQuery({ id: id, contain: ['Aulas'] });

  const enviar = async (dados: Treinamento): Promise<void> => {
    try {
      await editarTreinamento(dados).unwrap();

      toast({ conteudo: 'Editado com sucesso!', tema: 'success' });
    } catch (error) {
      toast({
        titulo: 'Erro ao editar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const enviarAula = async (dados: Aula): Promise<void> => {
    try {
      await adicionarAula({
        ...dados,
        treinamento_id: treinamento?.id,
      }).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      setModal(false);
    } catch (error) {
      toast({
        titulo: 'Erro ao editar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Editando treinamento"
        descricao="Altere o formulário e clique em enviar para editar o treinamento"
      />
      <Form
        onSubmit={enviar}
        inicial={treinamento}
        desabilitado={isLoading}
        regras={regras}
      >
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="bg-white shadow-sm rounded p-4">
              <div className="row">
                <div className="col-md-8">
                  <Campo name="titulo" rotulo="Título" />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="departamento_id"
                    rotulo="Departamento"
                    opcoes={departamentos}
                  />
                </div>
              </div>
              <CaixaDeTexto name="descricao" rotulo="Descrição" />
              <div className="mt-5">
                <BotaoForm>Enviar</BotaoForm>
                <BotaoLink tema="light" to="/treinamentos" className="ms-2">
                  Cancelar
                </BotaoLink>
              </div>
            </div>
            {treinamento?.aulas?.length ? (
              <div className="mt-3">
                <h3>Aulas disponíveis para o treinamento</h3>
                <div className="bg-white shadow-sm rounded">
                  {treinamento.aulas.map((aula) => (
                    <AulaLista
                      key={`${aula.id}-${aula.modificado}`}
                      dados={aula}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </Form>
      <div className="d-flex align-items-center justify-content-center mt-4">
        <Botao tema="success" icone="plus-lg" onClick={() => setModal(true)}>
          Adicionar aula
        </Botao>
      </div>
      <Modal titulo="Adicionando aula" exibir={modal} aoFechar={setModal}>
        <Form onSubmit={enviarAula} regras={regrasAula}>
          <Campo name="titulo" rotulo="Título" />
          <CaixaDeTexto name="descricao" rotulo="Descrição" />
          <Arquivo name="url" />
          <div className="mt-5">
            <BotaoForm>Enviar</BotaoForm>
            <Botao
              tema="light"
              onClick={() => setModal(false)}
              className="ms-2"
            >
              Cancelar
            </Botao>
          </div>
        </Form>
      </Modal>
    </LayoutDefault>
  );
};

export default Editar;
